import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';

import {
  handleOpenMenuMobile,
  handleScrollVisible,
  handleVisibleHeader,
  headerColorSchemeSelector,
  isLoadingSelector,
  scrollUpdateSelector,
  visibleHeaderSelector,
} from '../../entities/ComponentsSlice';
import { deviceSelector } from '../../entities/UserSlice';
import HeaderLogo from '../../features/header-logo/HeaderLogo';
import ROUTES from '../../layout/routes';
import style from './Header.module.scss';
import HeaderBar from './header-bar/HeaderBar';

function Header() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const headerColorScheme = useSelector(headerColorSchemeSelector);
  const isLoading = useSelector(isLoadingSelector);
  let scrollStart = 0;
  const device = useSelector(deviceSelector);
  const headerRef = useRef();

  const toggle = useSelector(visibleHeaderSelector);
  const [toggleWhite] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        dispatch(handleVisibleHeader(true));
      }, 2000);
    }
  }, [isLoading, dispatch]);

  const animatedStyle = useSpring({
    config: {
      duration: 800,
      mass: 1,
      tension: 280,
      friction: 120,
    },
    top: toggle ? '0px' : '-300px',
    delay: 0,
  });

  const hidingHeader = () => {
    scrollStart = scroll.scroll.instance.delta ? scroll.scroll.instance.delta.y : undefined;
    if (scroll.scroll.instance.scroll.y > 120) {
      if (scroll.scroll.instance.scroll.y < scrollStart) {
        dispatch(handleVisibleHeader(false));
      } else if (scroll.scroll.instance.scroll.y > scrollStart) {
        dispatch(handleVisibleHeader(true));
      } else return;
    }
  };

  const hidingHeaderMob = () => {
    const st = window.scrollY;
    if (window.scrollY > 90) {
      if (st > scrollStartMob) {
        dispatch(handleVisibleHeader(false));
        scrollStartMob = st;
      } else if (st <= scrollStartMob) {
        dispatch(handleVisibleHeader(true));
        scrollStartMob = st;
      } else scrollStartMob = st;
    }
  };

  const { scroll } = useLocomotiveScroll();

  let scrollStartMob = 0;
  const scrollUpdate = useSelector(scrollUpdateSelector);

  useLayoutEffect(() => {
    if (scroll && device !== 'isMobile' && pathname !== ROUTES.flats.list) {
      scroll.on('scroll', () => hidingHeader());
    } else if ((!scroll && device === 'isMobile') || (device !== 'isMobile' && pathname === ROUTES.flats.list)) {
      window.addEventListener('scroll', () => hidingHeaderMob());
    }
  }, [scroll, pathname, device, scrollUpdate]);

  useEffect(() => {
    if (pathname !== ROUTES.root) {
      dispatch(handleVisibleHeader(true));
      if (device === 'isMobile') {
        window.scrollTo(0, 0);
      }
    } else if (pathname === ROUTES.root && device === 'isMobile') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  const handleScrollTop = useCallback(() => {
    if (device === 'isMobile' && pathname === ROUTES.root) {
      dispatch(handleScrollVisible(true));
      window.scrollTo(0, 0);
      setTimeout(() => {
        dispatch(handleScrollVisible(false));
        dispatch(handleVisibleHeader(true));
      }, 500);
    } else if (device !== 'isMobile' && pathname === ROUTES.root) {
      dispatch(handleScrollVisible(true));
      headerRef.current.hidden = true;
      scroll?.scrollTo(0, 0);
      setTimeout(() => {
        dispatch(handleScrollVisible(false));
        headerRef.current.hidden = false;
        dispatch(handleVisibleHeader(true));
      }, 1500);
    }
  }, [device, pathname, scroll]);

  const chooseContainerColor = useCallback(() => {
    if (
      headerColorScheme === 'blackOnTransparent' ||
      headerColorScheme === 'blackOnTransparentTwo' ||
      headerColorScheme === 'blackOnTransparentThree'
    ) {
      return `${style.headerContainer} ${style.blackOnTransparent}`;
    }
    if (headerColorScheme === 'whiteOnTransparent') {
      return `${style.headerContainer} ${style.whiteOnTransparent}`;
    }
    if (headerColorScheme === 'multiOnTransparent') {
      return `${style.headerContainer} ${style.multiOnTransparent}`;
    }

    return `${style.headerContainer} ${style.headerContainer__white}`;
  }, [toggleWhite, headerColorScheme]);

  const whiteOrBlackContainer = chooseContainerColor();

  const chooseBurgerColor = useCallback(() => {
    if (headerColorScheme === 'whiteOnTransparent') {
      return 'white';
    }
    if (headerColorScheme === '' || headerColorScheme === 'blackOnTransparent') {
      return 'black';
    }
    return 'black';
  }, [headerColorScheme]);

  const burgerColor = chooseBurgerColor();

  return (
    <animated.div
      data-scroll
      data-scroll-sticky
      ref={headerRef}
      data-scroll-target='.target'
      className={whiteOrBlackContainer}
      style={{ ...animatedStyle }}
    >
      <div className={style.headerBox}>
        {headerColorScheme === 'whiteOnTransparent' ? (
          <HeaderLogo color='white' handleScrollTop={handleScrollTop} />
        ) : (
          <HeaderLogo color='#1C1C1C' handleScrollTop={handleScrollTop} />
        )}

        {device !== 'isMobile' && <HeaderBar toggleWhite={toggleWhite} headerColorScheme={headerColorScheme} />}
        {burgerColor === 'white' && device === 'isMobile' && (
          <div
            className={style.headerBurgerMenu}
            onClick={() => dispatch(handleOpenMenuMobile(true))}
            role='button'
            tabIndex='0'
          >
            <div className={style.whiteBurger} />
            <div className={style.whiteBurger} />
            <div className={style.whiteBurger} />
          </div>
        )}
        {burgerColor === 'black' && device === 'isMobile' && (
          <div
            className={style.headerBurgerMenu}
            onClick={() => dispatch(handleOpenMenuMobile(true))}
            role='button'
            tabIndex='0'
          >
            <div className={style.blackBurger} />
            <div className={style.blackBurger} />
            <div className={style.blackBurger} />
          </div>
        )}
      </div>
    </animated.div>
  );
}

export default Header;
